import { useEffect, useMemo, useState } from 'react';
import { estSimule, nonLusVides, type NotificationLue, type TakiSdk } from '@taki/sdk';
import type { NotificationBoiteView, NotificationsPort, NotificationView } from '@taki/ui';

/** L'hote relie `sdk.notifications` au panneau du shell, sans donner Firebase aux ecrans du socle. */
export function useNotificationsDeLHote(sdk: TakiSdk, email: string | null, canManage: boolean, isOwner: boolean): NotificationsPort {
  const [reglages, setReglages] = useState<NotificationsPort['reglages']>({
    existe: false, replyTo: null, nomExpediteur: null, piedDePage: null,
    canaux: { email: { actif: true }, whatsapp: { actif: false }, slack: { actif: false } },
  });
  const [prefs, setPrefs] = useState<NotificationsPort['prefs']>({ canaux: {}, silence: null, desinscrit: [], mascotteAnimee: true, notificationsExternes: {} });
  const [regles, setRegles] = useState<NotificationsPort['regles']>([]);
  const [ready, setReady] = useState(false), [prefsPretes, setPrefsPretes] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [preferencesConversations, setPreferencesConversations] = useState<import('@taki/ui').PreferenceConversationView[]>([]);
  useEffect(() => { setPreferencesConversations([]); return email ? sdk.notifications.conversationsPrefs.observer(setPreferencesConversations, e => setErreur(String(e))) : undefined; }, [sdk, email]);
  const [nonLus, setNonLus] = useState<NotificationsPort['nonLus']>(nonLusVides());

  useEffect(() => {
    if (email === null) { setReady(false); return; }
    setReady(false); setPrefsPretes(false); setErreur(null); setPrefs({ canaux: {}, silence: null, desinscrit: [], mascotteAnimee: true, notificationsExternes: {} });
    const arrets = [
      sdk.notifications.reglages.observer((r) => { setReglages(r); setReady(true); }, () => setReady(true)),
      sdk.notifications.regles.observer((r) => setRegles(r), () => setRegles([])),
      sdk.notifications.prefs.observer((p) => { setPrefs({ canaux: p.canaux, silence: p.silence, desinscrit: p.desinscrit, mascotteAnimee: p.mascotteAnimee, notificationsExternes: p.notificationsExternes }); setPrefsPretes(true); }, e => setErreur(String(e))),
    ];
    return () => { for (const arreter of arrets) arreter(); };
  }, [sdk, email]);

  /* Les non lus de la boite interne : la cloche du dock et le centre lisent ce meme total. */
  useEffect(() => {
    if (email === null) { setNonLus(nonLusVides()); return; }
    setNonLus(nonLusVides());
    return sdk.notifications.boite.observerNonLus(setNonLus, e => setErreur(String(e)));
  }, [sdk, email]);

  return useMemo<NotificationsPort>(() => ({
    preferencesConversations,
    enregistrerPreferenceConversation: p => sdk.notifications.conversationsPrefs.enregistrer(p),
    ready: ready && prefsPretes,
    erreur,
    reglages,
    prefs,
    regles,
    canManage,
    isOwner,
    enregistrerReglages: (patch) => sdk.notifications.reglages.enregistrer(patch),
    enregistrerPrefs: (patch) => sdk.notifications.prefs.enregistrer(patch),
    creerRegle: (input) => sdk.notifications.regles.creer(input),
    modifierRegle: (id, patch) => sdk.notifications.regles.modifier(id, patch),
    supprimerRegle: (id) => sdk.notifications.regles.supprimer(id),
    journal: async (limit) => (await sdk.notifications.journal.list({ limit: limit ?? 50 })).map(enVue),
    journalPeriode: async (depuis, limite) => (await sdk.notifications.journal.periode({ depuis, ...(limite !== undefined ? { limite } : {}) })).map(enVue),
    miens: async (limit) => (await sdk.notifications.journal.miens({ limit: limit ?? 20 })).map(enVue),
    envoyerEssai: (canal) => sdk.notifications.envoyerEssai(canal),
    testerRegle: (ruleId, recordId) => sdk.notifications.testerRegle(ruleId, recordId),
    passage: () => sdk.notifications.passage(),
    nonLus,
    boite: async (options) => (await sdk.notifications.boite.lister(options)).map(enBoite),
    marquerLu: (id) => sdk.notifications.boite.marquerLu(id),
    marquerLusDe: (objet) => sdk.notifications.boite.marquerLusDe(objet),
    toutMarquerLu: () => sdk.notifications.boite.toutMarquerLu(),
  }), [ready, prefsPretes, erreur, reglages, prefs, regles, canManage, isOwner, nonLus, sdk, preferencesConversations]);
}

function enBoite(n: NotificationLue): NotificationBoiteView {
  return {
    id: n.id,
    rubrique: n.rubrique ?? 'message',
    sujet: n.sujet,
    corps: n.corps,
    chemin: n.chemin ?? '',
    objet: n.objet ?? null,
    contexte: n.contexte
      ? { conversationId: n.contexte.conversationId, messageId: n.contexte.messageId, type: n.contexte.type, ancre: n.contexte.ancre ?? null, lieu: n.contexte.lieu ?? '' }
      : null,
    lu: n.luAt != null,
    createdAt: n.createdAt,
  };
}

function enVue(n: NotificationLue): NotificationView {
  return {
    id: n.id,
    canal: n.canal,
    destinataire: n.destinataire,
    sujet: n.sujet,
    statut: n.statut,
    derniereErreur: n.derniereErreur ?? null,
    tentatives: n.tentatives.length,
    source: {
      genre: n.source.genre,
      ...(n.source.ruleId ? { ruleId: n.source.ruleId } : {}),
      ...(n.source.famille ? { famille: n.source.famille } : {}),
      ...(n.source.recordId ? { recordId: n.source.recordId } : {}),
    },
    createdAt: n.createdAt,
    updatedAt: n.updatedAt,
    destinataireEmail: n.destinataireEmail ?? null,
    rubrique: n.rubrique ?? null,
    chemin: n.chemin ?? '',
    objet: n.objet ?? null,
    contexte: n.contexte ? { ...n.contexte, ancre: n.contexte.ancre ?? null, lieu: n.contexte.lieu ?? '' } : null,
    simule: estSimule(n),
    prochaineTentative: n.prochaineTentative,
    evenements: n.evenements.map((e) => e.type),
  };
}
